import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    ArrowLeft, ShieldCheck, Lock, CreditCard,
    Smartphone, Wallet, CheckCircle, Loader2, AlertCircle
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { useCart } from '../contexts/CartContext';
import { supabase } from '../lib/supabase';

const Checkout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { language } = useLanguage();
    const { user } = useAuth();
    const { showModal } = useNotification();
    const { clearCart } = useCart();
    const [isProcessing, setIsProcessing] = useState(false);
    const [orderDetails, setOrderDetails] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [cardNumber, setCardNumber] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [cvc, setCvc] = useState('');

    React.useEffect(() => {
        const state = location.state;
        if (!state || !state.amount || !state.items) {
            showModal(
                language === 'tr' ? 'Hata' : 'Error',
                language === 'tr' ? 'Sipariş bilgileri bulunamadı.' : 'Order details not found.',
                'error'
            );
            navigate('/basket');
            return;
        }

        if (!user) {
            showModal(
                language === 'tr' ? 'Giriş Gerekli' : 'Login Required',
                language === 'tr' ? 'Ödeme yapmak için giriş yapmalısınız.' : 'You must be logged in to make a payment.',
                'info'
            );
            navigate('/auth/login');
            return;
        }

        setOrderDetails(state);
    }, [location, user, navigate, language, showModal]);

    const formatCardNumber = (value) => {
        const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
        const matches = v.match(/\d{4,16}/g);
        const match = (matches && matches[0]) || '';
        const parts = [];

        for (let i = 0, len = match.length; i < len; i += 4) {
            parts.push(match.substring(i, i + 4));
        }

        if (parts.length) {
            return parts.join(' ');
        } else {
            return value;
        }
    };

    const formatExpiryDate = (value) => {
        const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
        if (v.length >= 2) {
            return v.substring(0, 2) + ' / ' + v.substring(2, 4);
        }
        return v;
    };

    const handleCardNumberChange = (e) => {
        const formatted = formatCardNumber(e.target.value);
        if (formatted.replace(/\s/g, '').length <= 16) {
            setCardNumber(formatted);
        }
    };

    const handleExpiryChange = (e) => {
        const formatted = formatExpiryDate(e.target.value);
        if (formatted.replace(/\s|\//g, '').length <= 4) {
            setExpiryDate(formatted);
        }
    };

    const handleCvcChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/gi, '');
        if (value.length <= 4) {
            setCvc(value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (paymentMethod === 'card') {
            if (!cardNumber || !expiryDate || !cvc) {
                showModal(
                    language === 'tr' ? 'Eksik Bilgi' : 'Incomplete Information',
                    language === 'tr' ? 'Lütfen tüm kart bilgilerini doldurun.' : 'Please fill in all card details.',
                    'error'
                );
                return;
            }
        }

        setIsProcessing(true);

        try {
            // Simulate payment processing
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Save purchased items to database
            // Prepare items for batch insert
            const itemsToInsert = orderDetails.items.map(item => ({
                user_id: user.id,
                service_name: item.name,
                service_type: item.type,
                status: 'active',
                price: item.price,
                currency: 'USD',
                billing_cycle: item.type === 'domain' ? 'annually' : 'monthly',
                purchase_date: new Date().toISOString(),
                expiry_date: item.type === 'domain'
                    ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
                    : null,
                metadata: item.metadata || {}
            }));

            // Save all items in a single request
            const { error } = await supabase.from('user_services').insert(itemsToInsert);

            if (error) {
                console.error('Insert error:', error);
                throw error;
            }

            clearCart();
            showModal(
                language === 'tr' ? 'Başarılı' : 'Success',
                language === 'tr' ? 'Ödemeniz başarıyla tamamlandı!' : 'Your payment was successful!',
                'success'
            );
            navigate('/portal');
        } catch (error) {
            console.error('Payment error:', error);
            showModal(
                language === 'tr' ? 'Ödeme Hatası' : 'Payment Error',
                error.message || (language === 'tr' ? 'Ödeme işlemi sırasında bir hata oluştu.' : 'An error occurred during payment.'),
                'error'
            );
        } finally {
            setIsProcessing(false);
        }
    };

    if (!orderDetails) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                        {language === 'tr' ? 'Yükleniyor...' : 'Loading...'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-32 pb-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/basket')}
                        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-6"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        {language === 'tr' ? 'Sepete Dön' : 'Back to Cart'}
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        {language === 'tr' ? 'Ödeme' : 'Checkout'}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">
                        {language === 'tr' ? 'Güvenli ödeme için bilgilerinizi girin' : 'Enter your payment information securely'}
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Payment Form */}
                    <div className="lg:col-span-2">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Payment Method Selection */}
                            <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                                    {language === 'tr' ? 'Ödeme Yöntemi' : 'Payment Method'}
                                </h3>

                                <div className="grid grid-cols-3 gap-3 mb-6">
                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('card')}
                                        className={`p-4 rounded-xl border-2 transition-all ${paymentMethod === 'card'
                                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                                            }`}
                                    >
                                        <CreditCard className={`w-6 h-6 mx-auto mb-2 ${paymentMethod === 'card' ? 'text-blue-600' : 'text-gray-400'}`} />
                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                            {language === 'tr' ? 'Kart' : 'Card'}
                                        </span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('apple_pay')}
                                        className={`p-4 rounded-xl border-2 transition-all ${paymentMethod === 'apple_pay'
                                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                                            }`}
                                    >
                                        <Smartphone className={`w-6 h-6 mx-auto mb-2 ${paymentMethod === 'apple_pay' ? 'text-blue-600' : 'text-gray-400'}`} />
                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Apple Pay</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setPaymentMethod('google_pay')}
                                        className={`p-4 rounded-xl border-2 transition-all ${paymentMethod === 'google_pay'
                                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                                            }`}
                                    >
                                        <Wallet className={`w-6 h-6 mx-auto mb-2 ${paymentMethod === 'google_pay' ? 'text-blue-600' : 'text-gray-400'}`} />
                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Google Pay</span>
                                    </button>
                                </div>

                                {/* Card Form */}
                                {paymentMethod === 'card' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                {language === 'tr' ? 'Kart Numarası' : 'Card Number'}
                                            </label>
                                            <input
                                                type="text"
                                                value={cardNumber}
                                                onChange={handleCardNumberChange}
                                                placeholder="4242 4242 4242 4242"
                                                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    {language === 'tr' ? 'Son Kullanma Tarihi' : 'Expiry Date'}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={expiryDate}
                                                    onChange={handleExpiryChange}
                                                    placeholder="MM / YY"
                                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    CVC
                                                </label>
                                                <input
                                                    type="text"
                                                    value={cvc}
                                                    onChange={handleCvcChange}
                                                    placeholder="123"
                                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Digital Wallet Messages */}
                                {paymentMethod === 'apple_pay' && (
                                    <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                                        <Smartphone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-blue-900 dark:text-blue-100">
                                            {language === 'tr'
                                                ? 'Apple Pay ile ödeme yapmak için "Öde" butonuna tıklayın.'
                                                : 'Click "Pay" to complete your purchase with Apple Pay.'}
                                        </div>
                                    </div>
                                )}

                                {paymentMethod === 'google_pay' && (
                                    <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                                        <Wallet className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-blue-900 dark:text-blue-100">
                                            {language === 'tr'
                                                ? 'Google Pay ile ödeme yapmak için "Öde" butonuna tıklayın.'
                                                : 'Click "Pay" to complete your purchase with Google Pay.'}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Lock className="w-4 h-4" />
                                <span>{language === 'tr' ? 'Ödemeniz 256-bit SSL ile güvenli bir şekilde işlenir' : 'Your payment is processed securely with 256-bit SSL'}</span>
                            </div>

                            <button
                                type="submit"
                                disabled={isProcessing}
                                className="w-full py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl font-bold hover:shadow-2xl hover:shadow-blue-500/20 transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                            >
                                {isProcessing ? (
                                    <>
                                        <Loader2 className="w-6 h-6 animate-spin" />
                                        {language === 'tr' ? 'İşleniyor...' : 'Processing...'}
                                    </>
                                ) : (
                                    <>
                                        <CreditCard className="w-6 h-6" />
                                        {language === 'tr' ? `$${orderDetails.amount.toFixed(2)} Öde` : `Pay $${orderDetails.amount.toFixed(2)}`}
                                    </>
                                )}
                            </button>

                            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-800">
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <ShieldCheck className="w-4 h-4 text-green-500" />
                                    <span>{language === 'tr' ? 'Güvenli' : 'Secure'}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <Smartphone className="w-4 h-4 text-blue-500" />
                                    <span>{language === 'tr' ? 'Dijital Cüzdanlar' : 'Digital Wallets'}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <Wallet className="w-4 h-4 text-purple-500" />
                                    <span>{language === 'tr' ? 'Tüm Kartlar' : 'All Cards'}</span>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 sticky top-32">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                                {language === 'tr' ? 'Sipariş Özeti' : 'Order Summary'}
                            </h3>

                            <div className="space-y-3 mb-6">
                                {orderDetails.items.map((item, index) => (
                                    <div key={index} className="flex justify-between text-sm">
                                        <span className="text-gray-600 dark:text-gray-400">{item.name}</span>
                                        <span className="font-semibold text-gray-900 dark:text-white">${item.price}</span>
                                    </div>
                                ))}
                            </div>

                            {orderDetails.discount > 0 && (
                                <div className="flex justify-between text-sm text-green-600 mb-2">
                                    <span>{language === 'tr' ? 'İndirim' : 'Discount'}</span>
                                    <span>-${orderDetails.discount.toFixed(2)}</span>
                                </div>
                            )}

                            <div className="pt-4 border-t border-gray-200 dark:border-gray-800 flex justify-between items-center">
                                <span className="text-lg font-bold text-gray-900 dark:text-white">
                                    {language === 'tr' ? 'Toplam' : 'Total'}
                                </span>
                                <span className="text-2xl font-bold text-blue-600">
                                    ${orderDetails.amount.toFixed(2)}
                                </span>
                            </div>

                            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800 space-y-3">
                                <div className="flex items-start gap-2 text-xs text-gray-500">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                    <span>{language === 'tr' ? 'Anında aktivasyon' : 'Instant activation'}</span>
                                </div>
                                <div className="flex items-start gap-2 text-xs text-gray-500">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                    <span>{language === 'tr' ? '30 gün para iade garantisi' : '30-day money-back guarantee'}</span>
                                </div>
                                <div className="flex items-start gap-2 text-xs text-gray-500">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                    <span>{language === 'tr' ? '7/24 müşteri desteği' : '24/7 customer support'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
